import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import * as storage from '../utils/storage';
import { setLogoutCallback } from '../api/client';
import { unsubscribePush } from '../api/push';

interface AuthContextType {
  loginKey: string | null;
  email: string | null;
  setLoginKey: (key: string | null) => void;
  setEmail: (email: string | null) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [loginKey, setLoginKeyState] = useState<string | null>(null);
  const [email, setEmailState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedLoginKey = storage.getLoginKey();
    const storedEmail = storage.getEmail();

    if (storedLoginKey) {
      setLoginKeyState(storedLoginKey);
    }
    if (storedEmail) {
      setEmailState(storedEmail);
    }

    setIsLoading(false);
  }, []);

  const setLoginKey = useCallback((key: string | null) => {
    setLoginKeyState(key);
    storage.setLoginKey(key);
  }, []);

  const setEmail = useCallback((newEmail: string | null) => {
    setEmailState(newEmail);
    storage.setEmail(newEmail);
  }, []);

  const logout = useCallback(() => {
    if (loginKey) {
      unsubscribePush(loginKey).catch(() => {});
    }
    setLoginKey(null);
  }, [loginKey, setLoginKey]);

  useEffect(() => {
    setLogoutCallback(logout);
  }, [logout]);

  const value: AuthContextType = {
    loginKey,
    email,
    setLoginKey,
    setEmail,
    logout,
    isAuthenticated: !!loginKey,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
